import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

import React, { useContext } from 'react';
import { CardsContext } from '@/app/components/search/components/SearchPageContainer/ListContainer/CardsGrid/CardsContext.ts';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
// eslint-disable-next-line import/no-cycle
import { getFeatures } from '@/app/components/search/components/Card/Details/Features/getFeatures';
import FeaturesIterator from '@/app/components/search/components/Card/Details/Features/FeaturesIterator';
import {
  DatesContainer,
  FacilitiesContainer,
  FeaturesContainer,
  TransportFeaturesContainer,
} from '@/app/components/search/components/Card/Details/Features/styled.ts';

type FeaturesProps = {
  dealCard?: DealCard;
  isOverlay: boolean;
  promotionTypes: PROMOTION_TYPES[];
};

const Features = ({ dealCard, promotionTypes, isOverlay }: FeaturesProps) => {
  const isClient = useIsClient();
  const { searchInfoData, searchInfoDetailData, localization, curiosityParams, searchParams, commonData } =
    usePageWrapperData();

  const { isWaiting, isLoading } = useContext(CardsContext);
  const { hasPreselectedFlightLink } = useSearchDealsPageData();
  const { t } = useTranslation();
  const transportData = dealCard?.transport?.data?.find(
    (d) => d.transportId === dealCard?.product?.transport?.transportId
  );

  const {
    dates = [],
    inlineFeatures = [],
    features = [],
    transportFeatures = [],
  } = getFeatures({
    dealCard,
    t,
    commonData,
    localization,
    searchParams,
    isWaiting,
    isLoading,
    isOverlay,
    promotionTypes,
    curiosityParams,
    searchInfoData,
    searchInfoDetailData,
    hasPreselectedFlightLink,
    transportData,
    isClient,
  });

  const isDP = searchInfoData.searchMode === SearchModes.DP;
  const isTH = searchInfoData.searchMode === SearchModes.TH;
  const isDoublePricing = isDP && !!localization?.configValuesApp?.taxesIncludedDoublePricing;
  const showDates = dates && dates.length > 0;
  const showInlineFeatures = inlineFeatures && inlineFeatures.length > 0 && !isOverlay;
  const showTransportFeatures = transportFeatures && transportFeatures.length > 0 && !isOverlay && (isDP || isTH);

  const showFeatures = features && features.length > 0;

  return (
    <>
      {showDates && (
        <DatesContainer>
          <FeaturesIterator features={dates} />
        </DatesContainer>
      )}
      {showInlineFeatures && (
        <FacilitiesContainer>
          <FeaturesIterator features={inlineFeatures} />
        </FacilitiesContainer>
      )}
      {showFeatures && (
        <FeaturesContainer>
          <FeaturesIterator features={features} />
        </FeaturesContainer>
      )}
      {showTransportFeatures && (
        <TransportFeaturesContainer $doublePricing={isDoublePricing}>
          <FeaturesIterator features={transportFeatures} />
        </TransportFeaturesContainer>
      )}
    </>
  );
};

export default Features;
